import React from 'react';
import PropTypes from 'prop-types';
import { Card, Row, Col, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { 
  FaListAlt, 
  FaFlask, 
  FaDatabase, 
  FaExchangeAlt, 
  FaTasks 
} from 'react-icons/fa';

/**
 * AugmentationRulesSection - Navigation hub for augmentation-related features
 * 
 * @param {string} customerId - The ID of the customer
 */
const AugmentationRulesSection = ({ customerId }) => {
  const navigate = useNavigate();

  const augmentationFeatures = [
    {
      title: 'Augmentation Rules',
      description: 'Manage augmentation rules for enhancing invoice data',
      icon: <FaListAlt size={32} />,
      path: `/customers/${customerId}/augmentation/rules`,
      color: 'primary'
    },
    {
      title: 'Rule Testing',
      description: 'Test and validate augmentation rules before deployment',
      icon: <FaFlask size={32} />,
      path: `/customers/${customerId}/augmentation/rule-testing`,
      color: 'success'
    },
    {
      title: 'Reference Data',
      description: 'Manage reference data used in augmentation rules',
      icon: <FaDatabase size={32} />,
      path: `/customers/${customerId}/augmentation/reference-data`,
      color: 'info'
    },
    {
      title: 'Aliases',
      description: 'Configure field and reference data aliases',
      icon: <FaExchangeAlt size={32} />,
      path: `/customers/${customerId}/augmentation/aliases`,
      color: 'warning'
    },
    {
      title: 'Review Queue',
      description: 'Review and approve augmentation results',
      icon: <FaTasks size={32} />,
      path: `/customers/${customerId}/augmentation/review-queue`,
      color: 'danger'
    }
  ];

  const handleNavigate = (path) => {
    navigate(path);
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 className="mb-0">Augmentation Features</h5>
      </div>

      <Row className="g-4">
        {augmentationFeatures.map((feature, index) => (
          <Col key={index} xs={12} md={6} lg={4}>
            <Card className="h-100 shadow-sm hover-shadow" style={{ cursor: 'pointer', transition: 'all 0.3s' }}>
              <Card.Body className="d-flex flex-column">
                <div className={`text-${feature.color} mb-3`}>
                  {feature.icon}
                </div>
                <Card.Title>{feature.title}</Card.Title>
                <Card.Text className="flex-grow-1 text-muted">
                  {feature.description}
                </Card.Text>
                <Button 
                  variant={`outline-${feature.color}`}
                  size="sm"
                  onClick={() => handleNavigate(feature.path)}
                  className="mt-2"
                >
                  Open {feature.title}
                </Button>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      <Card className="mt-4 bg-light">
        <Card.Body>
          <h6 className="mb-2">About Augmentation</h6>
          <p className="mb-0 text-muted small">
            Augmentation features allow you to enhance and enrich invoice data extracted from documents. 
            You can create rules to transform data, validate against reference data, and manage the review 
            process for augmented invoices. All augmentation features work within the context of customer 
            <strong> {customerId}</strong>.
          </p>
        </Card.Body>
      </Card>
    </div>
  );
};

AugmentationRulesSection.propTypes = {
  customerId: PropTypes.string.isRequired
};

export default AugmentationRulesSection;
